/**
 * Pure helpers for the Maintenance Report drawer (Safety V1 parity, Phase 2 Task 2.9).
 *
 * The save endpoint accepts ONLY the report keys that changed plus the controls
 * `approve` / `corrections_needed` / `deleted` + `delete_reason` (maintenance.py _save);
 * the defects endpoint upserts by `defectrecno` and 422s on unknown keys, so the drawer
 * posts only the changed rows with only the changed fields.
 */
import type { DefectSaveRow, MaintenanceDefectSection, MaintenanceSaveBody } from '../../../types/safetySystem';
import { diffChanges } from './unitForm';

/** Columns the backend accepts on `body.report` (maintenance.py REPORT_FIELDS). */
export const REPORT_FIELDS = [
  'unitnumber', 'driverrecno', 'truckrecno', 'maintmonth', 'maintyear', 'truckmileage', 'maintsubmitdate', 'maintfined',
] as const;

export const STATUS_OPTIONS = ['NOT SUBMITTED', 'OPEN', 'SUBMITTED', 'CORRECTIONS NEEDED', 'CLOSED'].map((v) => ({ value: v, label: v }));

const MONTHS = ['JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE', 'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'];
export const MONTH_OPTIONS = MONTHS.map((name, i) => ({ value: String(i + 1), label: name }));

const ZERO_DATES = new Set(['', '0', '0000-00-00', '0000-00-00 00:00:00', 'None']);
const norm = (v: unknown): string => (v === null || v === undefined ? '' : String(v));

export type DefectState = Record<number, { isdefect: string; defectcomments: string }>;

/** The editable copy of a report: only the postable columns, missing ones as ''. */
export function pickReportFields(report: Record<string, any>): Record<string, any> {
  const out: Record<string, any> = {};
  for (const f of REPORT_FIELDS) out[f] = report[f] === null || report[f] === undefined ? '' : report[f];
  return out;
}

/** Pre-save state of a brand-new report: current month / year, fined N (maintenance.py _new_report_defaults). */
export function newReportDefaults(now: Date = new Date()): Record<string, any> {
  return {
    unitnumber: '', driverrecno: 0, truckrecno: 0, maintmonth: now.getMonth() + 1, maintyear: now.getFullYear(),
    truckmileage: '', maintsubmitdate: '', maintfined: 'N',
  };
}

export const isApproved = (maintapprovedatetime: unknown): boolean => !ZERO_DATES.has(norm(maintapprovedatetime).trim());

/** Approve checkbox vs the loaded approval: `approve:true` to approve, `approve:false` to revoke, nothing when unchanged. */
export function approveControl(baseApproved: boolean, approved: boolean): { approve?: boolean } {
  if (approved === baseApproved) return {};
  return { approve: approved };
}

export function buildReportBody(
  base: Record<string, any>, current: Record<string, any>, baseApproved: boolean, approved: boolean,
): MaintenanceSaveBody {
  return { report: { ...diffChanges(base, current), ...approveControl(baseApproved, approved) } };
}

export function validateMonthYear(month: unknown, year: unknown): string | null {
  const m = Number(norm(month).trim());
  if (!Number.isInteger(m) || m < 1 || m > 12) return 'Month must be 1-12';
  const y = norm(year).trim();
  if (!/^\d{4}$/.test(y)) return 'Year must be 4 digits';
  return null;
}

export function monthName(month: unknown): string {
  const m = Number(norm(month).trim());
  return Number.isInteger(m) && m >= 1 && m <= 12 ? MONTHS[m - 1] : '';
}

/** Current year back six, as select options (newest first). */
export const yearOptions = (currentYear: number = new Date().getFullYear()) =>
  Array.from({ length: 7 }, (_, i) => String(currentYear - i)).map((y) => ({ value: y, label: y }));

export type StatusTone = 'accent' | 'warning' | 'neutral' | 'muted';

export function statusTone(status: unknown): StatusTone {
  switch (norm(status).trim().toUpperCase()) {
    case 'OPEN':
    case 'SUBMITTED':
      return 'accent';
    case 'CORRECTIONS NEEDED':
      return 'warning';
    case 'CLOSED':
      return 'neutral';
    default:
      return 'muted';
  }
}

export const STATUS_TONE_CLASS: Record<StatusTone, string> = {
  accent: 'bg-accent text-accent-fg',
  warning: 'bg-amber-100 text-amber-900 dark:bg-amber-900 dark:text-amber-100',
  neutral: 'bg-gray-200 text-heading dark:bg-gray-700',
  muted: 'border border-rule text-muted',
};

/** Loaded sections -> editable checklist state keyed by defectrecno. */
export function defectState(sections: MaintenanceDefectSection[]): DefectState {
  const out: DefectState = {};
  for (const s of sections ?? []) {
    for (const d of s.defects ?? []) {
      out[d.defectrecno] = { isdefect: norm(d.isdefect).toUpperCase() === 'Y' ? 'Y' : 'N', defectcomments: norm(d.defectcomments) };
    }
  }
  return out;
}

/** The checklist rows to PUT: only defects whose flag or comment changed, with only the changed keys. */
export function changedDefects(sections: MaintenanceDefectSection[], current: DefectState): DefectSaveRow[] {
  const base = defectState(sections);
  const out: DefectSaveRow[] = [];
  for (const [key, cur] of Object.entries(current)) {
    const defectrecno = Number(key);
    const orig = base[defectrecno] ?? { isdefect: 'N', defectcomments: '' };
    const row: DefectSaveRow = { defectrecno };
    const flag = norm(cur.isdefect).toUpperCase() === 'Y' ? 'Y' : 'N';
    if (flag !== orig.isdefect) row.isdefect = flag;
    if (norm(cur.defectcomments) !== orig.defectcomments) row.defectcomments = norm(cur.defectcomments);
    if (row.isdefect !== undefined || row.defectcomments !== undefined) out.push(row);
  }
  return out;
}

export const defectsDirty = (sections: MaintenanceDefectSection[], current: DefectState): boolean =>
  changedDefects(sections, current).length > 0;
